import { lineString, polygon as tPolygon } from "@turf/helpers";
import { useMapStore } from "stores/map";

import { getPerpendicularLine } from "api/map";

import { setXdaLine } from "./mapboxHelper";

/**
 * Picks the well source for an XDA line request.
 *
 * `wellsIntersect` is the UWI→color map returned by the map tile intersection
 * query (individual well UWIs even in group mode). When it is non-empty it is
 * preferred over `selectedWells`, which in group mode contains synthetic group
 * entity IDs rather than real UWIs. Falls back to `selectedWells` (single-well
 * / pre-selected mode) when no intersection result is available.
 *
 * Returns the keys of the winning source as a plain string array, ready for the
 * XDA service payload.
 */
export function resolveXdaWells(
  selectedWells: Record<string, unknown> | null | undefined,
  wellsIntersect: Record<string, unknown> | null | undefined
): string[] {
  const source =
    wellsIntersect && Object.keys(wellsIntersect).length > 0
      ? wellsIntersect
      : selectedWells;
  return Object.keys(source ?? {});
}

export async function makeXdaLineRequest(
  geom,
  selectedWells = {},
  mapbox,
  dispatch,
  selectionPolygonRef,
  getAndSetFeaturesIntersectingPolygon
) {
  const coords = geom?.coordinates;
  if (!coords) {
    return;
  }
  const poly = tPolygon([
    [
      [coords[0][0], coords[0][1]],
      [coords[1][0], coords[1][1]],
      [coords[1][0], coords[1][1]],
      [coords[0][0], coords[0][1]]
    ]
  ]);
  geom = poly;
  setXdaLine(
    {
      start: { x: coords[0][0], y: coords[0][1] },
      end: { x: coords[1][0], y: coords[1][1] }
    },
    [],
    mapbox
  );
  selectionPolygonRef.current = geom;
  let wellsIntersect: Record<string, string> | undefined;
  if (!selectedWells || Object.keys(selectedWells).length === 0) {
    [selectedWells, wellsIntersect] = await getAndSetFeaturesIntersectingPolygon(poly);
  }
  useMapStore.getState().setXdaLineRequest({
    line: coords,
    wells: resolveXdaWells(selectedWells, wellsIntersect)
  });
}

export async function getPerpendicularLineAndMakeXdaRequest(
  uwi,
  feat,
  mapbox,
  dispatch,
  selectionPolygonRef,
  getAndSetFeaturesIntersectingPolygon
) {
  const selectedWells = {};

  if (feat) {
    selectedWells[uwi] = feat.properties;
    uwi = feat.properties.Uwi;
  }

  const perpedicularLine = await getPerpendicularLine(uwi);
  // Make sure to do null checks here otherwise it will failed with 0,0 coordinates
  if (
    perpedicularLine?.[0]?.x != null &&
    perpedicularLine?.[1]?.x != null &&
    perpedicularLine?.[0]?.y != null &&
    perpedicularLine?.[1]?.y != null
  ) {
    const feat = lineString([
      [perpedicularLine[0]?.x, perpedicularLine[0]?.y],
      [perpedicularLine[1]?.x, perpedicularLine[1]?.y]
    ]);
    await makeXdaLineRequest(
      feat.geometry,
      selectedWells,
      mapbox,
      dispatch,
      selectionPolygonRef,
      getAndSetFeaturesIntersectingPolygon
    );
  }
}
